/**
 * MitzvahUgcGallery — splits UGC card blocks into a featured item (first
 * child) and a horizontally-scrollable carousel (remaining children) on
 * mobile, and makes the whole surface of a video card toggle playback.
 *
 * The play/pause button itself is owned by the shared `video-media` element
 * (each video card IS one) — same component and control design as
 * full-screen-media. This element only adds the surface-click behavior and
 * localized aria-labels on top.
 *
 * On desktop the carousel wrapper uses `display: contents` via CSS, so every
 * card participates in the parent flex row as equal-width siblings.
 */
class MitzvahUgcGallery extends HTMLElement {
  connectedCallback() {
    // Wait one frame so {% content_for 'blocks' %} has rendered
    requestAnimationFrame(() => {
      this.setupLayout();
      this.setupVideoCards();
    });
  }

  setupLayout() {
    const cards = Array.from(this.children).filter((el) => el.classList.contains('ugc-card'));
    if (cards.length < 2) return;

    const carousel = document.createElement('div');
    carousel.className = 'ugc__carousel';

    // Move every card after the first into the carousel wrapper
    for (let i = 1; i < cards.length; i++) {
      carousel.appendChild(cards[i]);
    }

    this.appendChild(carousel);
  }

  setupVideoCards() {
    this.querySelectorAll('.ugc-card--video').forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;

      // Whole card surface toggles playback. The corner button already
      // toggles via video-media; a linked card's overlay sits above the
      // surface, so the link wins there.
      card.addEventListener('click', (event) => {
        if (event.target.closest('[data-play-toggle]')) return;
        if (event.target.closest('.ugc-card__link')) return;
        if (typeof card.togglePlay === 'function') {
          card.togglePlay();
        } else if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });

      // Localized aria-label on top of video-media's icon sync
      const toggle = card.querySelector('[data-play-toggle]');
      if (toggle) {
        const syncLabel = () => {
          toggle.setAttribute(
            'aria-label',
            video.paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause
          );
        };
        video.addEventListener('play', syncLabel);
        video.addEventListener('pause', syncLabel);
        syncLabel();
      }
    });
  }
}

if (!customElements.get('mitzvah-ugc-gallery')) {
  customElements.define('mitzvah-ugc-gallery', MitzvahUgcGallery);
}
